import { useEffect, useRef } from "react";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import Progress from "@/components/Progress";
import Tag from "@/components/Tag";
import type { InteractionTopicInput } from "../queries";
import type { TopicFinding } from "../aiQueries";
import {
  isTopicLocked,
  subtopicErrorKey,
  suggestionKeyFor,
  type ChangedTopicFields,
  type TopicFieldChanges,
  type TopicValidationErrors
} from "../_utils/topicsValidation";

export type TopicFlash = {
  key: number;
  dept: string;
  changes: TopicFieldChanges;
  deleted: boolean;
} | null;

type TopicsEditorProps = {
  topics: InteractionTopicInput[];
  errors: TopicValidationErrors;
  focusKey: string | null;
  flash: TopicFlash;
  changed: ChangedTopicFields;
  findings: TopicFinding[];
  suggestingKey: string | null;
  onFocusApplied: () => void;
  onChangeTopicName: (topicIndex: number, name: string) => void;
  onChangeTopicDescription: (topicIndex: number, description: string) => void;
  onChangeSubtopicName: (topicIndex: number, subtopicIndex: number, name: string) => void;
  onChangeSubtopicDescription: (topicIndex: number, subtopicIndex: number, description: string) => void;
  onAddSubtopic: (topicIndex: number) => void;
  onRemoveSubtopic: (topicIndex: number, subtopicIndex: number) => void;
  onSuggestDescription: (topicIndex: number, subtopicIndex: number | null) => void;
  onAddTopic: () => void;
  onRemoveTopic: (topicIndex: number) => void;
};

export function topicFocusKey(topicIndex: number) {
  return `topic:${topicIndex}`;
}

// Unsaved edits tint input text brand blue via the page-level
// .topics-changed-field rule (input text only — never the border).
function changedFieldClass(isChanged: boolean | undefined): string {
  return isChanged ? "topics-changed-field w-full" : "w-full";
}

function SavedBadge({ flashKey, testId }: { flashKey: number; testId: string }) {
  return (
    <span
      key={flashKey}
      role="status"
      data-testid={testId}
      className="topics-save-flash pointer-events-none absolute -top-3 right-2 z-10 rounded-full border border-sys-brand-outline-variant bg-sys-brand-surface px-2 py-0.5 text-body-small font-medium text-sys-brand-primary shadow-sm"
    >
      ✓ Saved
    </span>
  );
}

function SuggestButton({
  loading,
  disabled,
  label,
  testId,
  onClick
}: {
  loading: boolean;
  disabled: boolean;
  label: string;
  testId: string;
  onClick: () => void;
}) {
  if (loading) {
    return <Progress size={18} />;
  }
  return (
    <IconButton
      name="psychology"
      skipMargin
      ariaLabel={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      iconClassName="text-sys-brand-primary"
    />
  );
}

function FindingCallout({ finding, testId }: { finding: TopicFinding; testId: string }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-1 rounded-md border border-sys-brand-outline-variant bg-sys-brand-surface p-3"
    >
      <div className="flex items-center gap-2">
        <Tag tagType={finding.severity === "warning" ? "warn" : "information"} size="small">
          {finding.severity === "warning" ? "AI: review" : "AI: suggestion"}
        </Tag>
      </div>
      <p className="text-body-small text-sys-brand-on-surface-variant">{finding.message}</p>
      {finding.suggestion ? (
        <p className="text-body-small text-sys-brand-on-surface">
          <span className="font-medium">Suggested fix: </span>
          {finding.suggestion}
        </p>
      ) : null}
    </div>
  );
}

export default function TopicsEditor({
  topics,
  errors,
  focusKey,
  flash,
  changed,
  findings,
  suggestingKey: activeSuggestionKey,
  onFocusApplied,
  onChangeTopicName,
  onChangeTopicDescription,
  onChangeSubtopicName,
  onChangeSubtopicDescription,
  onAddSubtopic,
  onRemoveSubtopic,
  onSuggestDescription,
  onAddTopic,
  onRemoveTopic
}: TopicsEditorProps) {
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    if (!focusKey) return;
    const input = inputRefs.current.get(focusKey);
    if (input) {
      input.focus();
      input.select();
    }
    onFocusApplied();
  }, [focusKey, onFocusApplied, topics]);

  const setRef = (key: string) => (element: HTMLInputElement | null) => {
    if (element) {
      inputRefs.current.set(key, element);
    } else {
      inputRefs.current.delete(key);
    }
  };

  // Single-flight: every sparkle button disables while any suggestion is
  // active; the active row keeps its loading spinner via its key match.
  const suggestionActive = activeSuggestionKey !== null;

  return (
    <div className="flex flex-col gap-6">
      {errors.formError ? (
        <p role="alert" data-testid="topics-form-error" className="text-body-medium text-sys-brand-error">
          {errors.formError}
        </p>
      ) : null}
      <div
        data-testid="topics-grid"
        className="grid grid-cols-1 items-start gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {topics.map((topic, topicIndex) => {
          // Locked Other rows render as static footer lines (below), never as
          // disabled inputs — state indexes are preserved for keys/handlers.
          if (isTopicLocked(topics, topicIndex)) return null;
          const topicError = errors.topicErrors[topicIndex];
          const topicDescriptionError = errors.topicDescriptionErrors[topicIndex];
          const subtopicError = errors.subtopicErrors[topicIndex];
          const flashFields = flash?.changes[topicIndex];
          const changedFields = changed[topicIndex];
          const topicFindings = findings.filter(
            (finding) => finding.topics.includes(topic.name.trim()) && finding.subtopics.length === 0
          );
          const topicName = topic.name.trim();
          return (
            <div
              key={topic.key ?? `topic-${topicIndex}`}
              data-testid={`topic-card-${topicIndex}`}
              role="group"
              aria-label={topicName ? `Topic: ${topicName}` : `Topic ${topicIndex + 1}`}
              className="flex flex-col gap-6 rounded-lg bg-sys-brand-surface-container-low p-4"
            >
              <div className="relative flex-1">
                {/* w-full on fields: the Material host is inline-flex
                    shrink-to-fit and these wrappers are not flex containers, so
                    flex-1 is inert and input/textarea intrinsic widths differ. */}
                <TextField
                  ref={setRef(topicFocusKey(topicIndex))}
                  label="Topic"
                  value={topic.name}
                  onChangeText={(value) => onChangeTopicName(topicIndex, value)}
                  error={Boolean(topicError)}
                  errorText={topicError}
                  trailingIcon={
                    <IconButton
                      name="delete"
                      skipMargin
                      ariaLabel={`Remove ${topic.name || "topic"}`}
                      onClick={() => onRemoveTopic(topicIndex)}
                    />
                  }
                  className={changedFieldClass(changedFields?.name)}
                />
                {flash && flashFields?.name ? (
                  <SavedBadge flashKey={flash.key} testId={`saved-badge-${topicIndex}-name`} />
                ) : null}
              </div>
              <div className="relative flex-1">
                <TextField
                  label="Topic description"
                  placeholder="What belongs under this topic?"
                  supportingText="Helps staff pick correctly and guides the AI classifier."
                  type="textarea"
                  rows={2}
                  value={topic.description ?? ""}
                  onChangeText={(value) => onChangeTopicDescription(topicIndex, value)}
                  error={Boolean(topicDescriptionError)}
                  errorText={topicDescriptionError}
                  trailingIcon={
                    <SuggestButton
                      loading={activeSuggestionKey === suggestionKeyFor(topic.key, null)}
                      disabled={!topicName || suggestionActive}
                      label={`Suggest a description for ${topicName || "this topic"}`}
                      testId={`suggest-topic-${topicIndex}`}
                      onClick={() => onSuggestDescription(topicIndex, null)}
                    />
                  }
                  className={changedFieldClass(changedFields?.description)}
                />
                {flash && flashFields?.description ? (
                  <SavedBadge flashKey={flash.key} testId={`saved-badge-${topicIndex}-description`} />
                ) : null}
              </div>
              {topicFindings.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {topicFindings.map((finding, findingIndex) => (
                    <FindingCallout
                      key={`${finding.topics.join("|")}|${finding.message}|${findingIndex}`}
                      finding={finding}
                      testId={`topic-finding-${topicIndex}-${findingIndex}`}
                    />
                  ))}
                </div>
              ) : null}
              <div className="flex flex-col gap-6">
                <div className="relative flex flex-col gap-6">
                  <div className="flex items-center justify-between">
                    <span className="text-body-medium font-medium text-sys-brand-on-surface">Subtopics</span>
                    <Button
                      variant="text"
                      icon="add"
                      onClick={() => onAddSubtopic(topicIndex)}
                      testId={`add-subtopic-${topicIndex}`}
                      ariaLabel={`Add subtopic to ${topicName || "topic"}`}
                    >
                      Add subtopic
                    </Button>
                  </div>
                  {topic.subtopics.length === 0 ? (
                    <p
                      data-testid={`no-subtopics-${topicIndex}`}
                      className="text-body-small text-sys-brand-on-surface-variant"
                    >
                      No subtopics yet — add one when this topic needs finer routing.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-6">
                      {topic.subtopics.map((subtopic, subtopicIndex) => {
                        const subDescriptionError =
                          errors.subtopicDescriptionErrors[subtopicErrorKey(topicIndex, subtopicIndex)];
                        const subChanged = changedFields?.subtopics[subtopicIndex];
                        const subFindings = findings.filter(
                          (finding) =>
                            finding.topics.includes(topicName) && finding.subtopics.includes(subtopic.name.trim())
                        );
                        return (
                          <div
                            key={subtopic.key ?? `sub-${topicIndex}-${subtopicIndex}`}
                            data-testid={`subtopic-row-${topicIndex}-${subtopicIndex}`}
                            role="group"
                            aria-label={
                              subtopic.name.trim()
                                ? `Subtopic: ${subtopic.name.trim()}`
                                : `Subtopic ${subtopicIndex + 1} of ${topicName || `topic ${topicIndex + 1}`}`
                            }
                            className="flex flex-col gap-6 rounded-md border border-sys-brand-outline-variant bg-sys-brand-surface p-3"
                          >
                            <TextField
                              label="Subtopic"
                              value={subtopic.name}
                              onChangeText={(value) => onChangeSubtopicName(topicIndex, subtopicIndex, value)}
                              trailingIcon={
                                <IconButton
                                  name="delete"
                                  skipMargin
                                  ariaLabel={`Remove ${subtopic.name || "subtopic"}`}
                                  onClick={() => onRemoveSubtopic(topicIndex, subtopicIndex)}
                                />
                              }
                              className={changedFieldClass(subChanged?.name)}
                            />
                            <TextField
                              label="Subtopic description"
                              placeholder="What belongs under this subtopic?"
                              type="textarea"
                              rows={2}
                              value={subtopic.description ?? ""}
                              onChangeText={(value) => onChangeSubtopicDescription(topicIndex, subtopicIndex, value)}
                              error={Boolean(subDescriptionError)}
                              errorText={subDescriptionError}
                              trailingIcon={
                                <SuggestButton
                                  loading={activeSuggestionKey === suggestionKeyFor(topic.key, subtopic.key)}
                                  disabled={!topicName || !subtopic.name.trim() || suggestionActive}
                                  label={`Suggest a description for ${subtopic.name.trim() || "this subtopic"}`}
                                  testId={`suggest-subtopic-${topicIndex}-${subtopicIndex}`}
                                  onClick={() => onSuggestDescription(topicIndex, subtopicIndex)}
                                />
                              }
                              className={changedFieldClass(subChanged?.description)}
                            />
                            {subFindings.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {subFindings.map((finding, findingIndex) => (
                                  <FindingCallout
                                    key={`${finding.topics.join("|")}|${finding.subtopics.join("|")}|${finding.message}|${findingIndex}`}
                                    finding={finding}
                                    testId={`subtopic-finding-${topicIndex}-${subtopicIndex}-${findingIndex}`}
                                  />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {subtopicError ? (
                    <p
                      role="alert"
                      data-testid={`subtopic-error-${topicIndex}`}
                      className="text-body-small text-sys-brand-error"
                    >
                      {subtopicError}
                    </p>
                  ) : null}
                  {flash && flashFields?.subtopics ? (
                    <SavedBadge flashKey={flash.key} testId={`saved-badge-${topicIndex}-subtopics`} />
                  ) : null}
                </div>
                {topic.subtopics.some((row) => row.name.trim().length > 0) ? (
                  <p className="text-body-small text-sys-brand-on-surface-variant">+ Subtopic: Other [always shown]</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-body-small text-sys-brand-on-surface-variant" data-testid="topics-other-footer">
        + Topic: Other [always shown]
      </p>
      <div>
        <Button variant="outlined" icon="add" onClick={onAddTopic} testId="add-topic">
          Add topic
        </Button>
      </div>
    </div>
  );
}
